<template>
  <div>
    <h4 class="tw-text-[#6d7287] tw-text-base tw-mb-5">
      {{ t('suppliers.titles.new_bank_account') }}
    </h4>

    <Card class="tw-p-8">
      <div
        class="tw-text-base tw-font-medium tw-mb-6 tw-uppercase tw-text-[#3f4456] tw-pb-4 tw-border-b tw-border-[#d7ddf5]"
      >
        {{ t('suppliers.titles.bank_account') }}
      </div>

      <form
        @submit="onSubmitBankAccountForm"
        class="tw-flex-1 tw-w-full tw-px-4 tw-flex tw-flex-col"
      >
        <!-- Owner and Bank Name -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label for="ownerName">
              {{ t('suppliers.titles.account_holder') }}
              <span class="tw-text-red-600">*</span>
            </Label>
            <Input
              v-model="ownerName"
              id="ownerName"
              name="ownerName"
              type="text"
              :class="{ 'tw-border-danger': errors.ownerName }"
              :placeholder="t('suppliers.titles.name_placeholder') + '...'"
            />
            <div v-if="errors.ownerName" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.ownerName }}
            </div>
          </div>

          <div>
            <Label for="bankName">
              {{ t('suppliers.titles.bank_name') }}
              <span class="tw-text-red-600">*</span>
            </Label>
            <Input
              v-model="bankName"
              id="bankName"
              name="bankName"
              type="text"
              :class="{ 'tw-border-danger': errors.bankName }"
              :placeholder="t('suppliers.titles.name_placeholder') + '...'"
            />
            <div v-if="errors.bankName" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.bankName }}
            </div>
          </div>
        </div>

        <!-- SWIFT Code and IBAN -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label for="swiftCode">
              {{ t('suppliers.titles.swift_placeholder') }}
              <span class="tw-text-red-600">*</span>
            </Label>
            <Input
              v-model="swiftCode"
              id="swiftCode"
              name="swiftCode"
              type="text"
              :class="{ 'tw-border-danger': errors.swiftCode }"
              :placeholder="t('suppliers.titles.swift_code_placeholder') + '...'"
            />
            <div v-if="errors.swiftCode" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.swiftCode }}
            </div>
          </div>

          <div>
            <Label for="iban">
              {{ t('suppliers.titles.iban') }}
              <span class="tw-text-red-600">*</span>
            </Label>
            <Input
              v-model="iban"
              id="iban"
              name="iban"
              type="text"
              :class="{ 'tw-border-danger': errors.iban }"
              :placeholder="t('suppliers.titles.iban_placeholder') + '...'"
            />
            <div v-if="errors.iban" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.iban }}
            </div>
          </div>
        </div>

        <!-- Supplier -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label for="supplierId">
              {{ t('suppliers.titles.suppliers') }}
              <span class="tw-text-red-600">*</span>
            </Label>
            <SupplierSelect
              v-model="supplierId"
              :customer-id="customerId"
              :page-size="200"
              :placeholder="t('suppliers.titles.select_supplier')"
              :class="{ 'tw-border-danger': errors.supplierId }"
            />
            <div v-if="errors.supplierId" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.supplierId }}
            </div>
          </div>
        </div>

        <div v-if="bankAccountCreatedSuccessfully != null" class="result-alert tw-mt-4">
          <Alert v-if="bankAccountCreatedSuccessfully && !meta.dirty" class="lg:tw-w-7/12">
            <AlertDescription>
              {{ t('suppliers.titles.bank_account_create_success') }}
            </AlertDescription>
          </Alert>
          <Alert v-if="!bankAccountCreatedSuccessfully" variant="destructive" class="lg:tw-w-7/12">
            <AlertDescription>{{ errorMessage }}</AlertDescription>
          </Alert>
        </div>

        <div class="tw-flex tw-gap-2 tw-justify-end">
          <Button
            type="button"
            variant="secondary"
            @click="backToBankAccountsList"
            :disabled="isSubmitting"
          >
            {{ t('common.actions.discard') }}
          </Button>
          <CustomerActionButton
            type="submit"
            class="tw-bg-primary-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm"
            :disabled="isSubmitting"
          >
            {{ t('suppliers.titles.add_bank_account') }}
          </CustomerActionButton>
        </div>
      </form>
    </Card>

    <ConfirmationModal
      v-model="showConflictModal"
      :title="t('suppliers.titles.attention').toUpperCase()"
      :message="conflictMessage"
      type="warning"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="onConfirmConflict"
      @cancel="onCancelConflict"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SupplierSelect from '@/components/shared/SupplierSelect.vue';
import { useSupplierBankAccountForm } from '@/_helpers/composables/master-data/supplier-bank-account-form.composable';
import { useBankAccountStore } from '@/stores/bank-account.store';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user.store';
import { useToast } from 'vue-toast-notification';
import { useI18n } from 'vue-i18n';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import { ConfirmationModal } from '@/components/shared';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useSupplierBankAccountConflict } from '@/_helpers/composables/master-data/supplier-bank-account-conflict.composable';
import cookieService from '@/_helpers/services/cookie.service';

const { currentUser } = storeToRefs(useUserStore());
const toast = useToast();
const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();

const {
  errors,
  handleSubmit,
  isSubmitting,
  meta,
  resetForm,
  ownerName,
  bankName,
  swiftCode,
  iban,
  customerId,
  supplierId,
  backToBankAccountsList,
} = useSupplierBankAccountForm();

const bankAccountCreatedSuccessfully: Ref<boolean | null> = ref(null);
const errorMessage = ref('');

const { createBankAccount } = useBankAccountStore();

const { showConflictModal, conflictMessage, findConflicts, buildConflictMessage } =
  useSupplierBankAccountConflict();
const pendingValues = ref<any>(null);

const onSubmitBankAccountForm = handleSubmit(async values => {
  bankAccountCreatedSuccessfully.value = null;
  errorMessage.value = '';
  const conflicts = await findConflicts(values.iban, values.customerId, values.supplierId);
  if (conflicts.length) {
    pendingValues.value = values;
    conflictMessage.value = buildConflictMessage(conflicts);
    showConflictModal.value = true;
    return;
  }
  await persistBankAccount(values);
});

const onConfirmConflict = async () => {
  if (!pendingValues.value) return;
  await persistBankAccount(pendingValues.value);
  pendingValues.value = null;
};

const onCancelConflict = () => {
  pendingValues.value = null;
};

const persistBankAccount = async (values: any) => {
  try {
    await createBankAccount({
      owner_name: values.ownerName,
      bank_name: values.bankName,
      swift_code: values.swiftCode,
      iban: values.iban,
      customer: values.customerId,
      supplier: values.supplierId,
    });

    let oldCustomerId = customerId.value;
    bankAccountCreatedSuccessfully.value = true;
    resetForm();
    const { value: user } = currentUser;
    if (!user?.user_role.includes('Admin')) {
      customerId.value = oldCustomerId;
    }
    supplierId.value = null;
    toast.open({
      message: t('suppliers.titles.bank_account_create_success'),
      type: 'success',
    });
  } catch (error: any) {
    bankAccountCreatedSuccessfully.value = false;
    errorMessage.value = error.response.data.errors[0].detail;
  }
};

onMounted(async () => {
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.bank_accounts',
      to: { name: 'bank-accounts' },
    },
    {
      labelKey: 'sidebar.items.supplier_bank_accounts',
      to: { name: 'list-supplier-bank-accounts' },
    },
    {
      labelKey: 'common.actions.create',
    },
  ]);
  customerId.value = +cookieService.get('selectedCustomerId')!;
});
</script>

<style scoped></style>
